/**
 * Switch Component
 *
 * Accessible toggle switch for binary on/off states.
 * Built with Radix UI for keyboard navigation and screen reader support.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, useId, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../../../lib/utils';

export interface SwitchProps extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label?: string;
  helperText?: string;
  error?: string;
}

/**
 * Switch Component
 *
 * @example
 * ```tsx
 * <Switch
 *   label="Enable notifications"
 *   helperText="Receive email updates"
 *   onCheckedChange={(checked) => console.log(checked)}
 * />
 * ```
 */
const Switch = forwardRef<ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, label, helperText, error, disabled, ...props }, ref) => {
    const autoId = useId();
    const switchId = props.id || autoId;

    return (
      <div className="flex items-start gap-3">
        <SwitchPrimitive.Root
          id={switchId}
          ref={ref}
          disabled={disabled}
          className={cn(
            // Base styles - BFSG compliant 44px height
            'group relative inline-flex h-11 w-20 shrink-0 cursor-pointer items-center rounded-full',
            'transition-colors duration-200 ease-in-out',

            // Background states
            'bg-neutral-200',
            'data-[state=checked]:bg-primary-600',

            // Focus visible ring - WCAG 2.1 AA
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',

            // Disabled state
            'disabled:cursor-not-allowed disabled:opacity-50',

            // Error state
            error && 'ring-2 ring-red-500',

            className
          )}
          {...props}
        >
          <SwitchPrimitive.Thumb
            className={cn(
              // Base thumb - 36px size (81.8% of track for proper contrast)
              'pointer-events-none block h-9 w-9 rounded-full bg-white shadow-lg',
              'transition-transform duration-200 ease-in-out',

              // Positioning
              'translate-x-1 data-[state=checked]:translate-x-10'
            )}
          />
        </SwitchPrimitive.Root>

        {/* Label and helper text - click to toggle */}
        {(label || helperText || error) && (
          <label
            htmlFor={switchId}
            className={cn(
              'flex flex-1 cursor-pointer flex-col gap-1',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {label && (
              <span className="text-sm font-medium text-neutral-900">
                {label}
              </span>
            )}
            {error && (
              <span className="text-sm text-red-600" role="alert">
                {error}
              </span>
            )}
            {helperText && !error && (
              <span className="text-sm text-neutral-600">{helperText}</span>
            )}
          </label>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';

export { Switch };
